import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { red } from "@mui/material/colors";
import arrayMutators from "final-form-arrays";
import camelCase from "lodash/camelCase";
import isNil from "lodash/isNil";
import { memo, useCallback } from "react";
import { Form } from "react-final-form";
import { camelCaseToSentence } from "../../utils/strings/camelCaseToSentence";
import Flx from "../layout/Flx";
import RffFormSpy from "./shared/RffFormSpy";
import RffGroup from "./shared/RffGroup";
import RffGroupContainer from "./shared/RffGroupContainer";

// The live-values JSON panel is ON by default while developing and OFF in a
// production build. It renders raw form values — borrower names, liquidity,
// addresses — so it must never reach a deployed bundle; `main` auto-deploys.
// A call site can still force it either way: `formSpy` / `formSpy={false}`.
const FORM_SPY_DEFAULT = process.env.NODE_ENV === "development";

function RffForm({
  formSpy = FORM_SPY_DEFAULT,
  id,
  onSubmit,
  children,
  initialValues,
  errorMessage,
  isLoading,
  onCancel,
  onCancelText = "Cancel",
  loading,
  titleSize = "md",
  maxWidth = false,
  formTitle,
  formDescription,
  className,
  suppressFooter,
  includeBottomSpacing,
  titleContent,
  flexDirection = "column",
  titleEndContent,
  footerStartContent,
  grid,
  disabled,
  startContent,
  gap = "44px",
  submitText = "Submit",
  sx,
  hideBottomValidationErrors = false,
  // Pins the submit bar to the bottom of the scrolling panel. Off by default:
  // a form that fills its container already ends with the footer at the bottom.
  // Turn it on where the form shares the panel with content ABOVE it (a chooser,
  // an import banner), which otherwise pushes the submit button below the fold.
  stickyFooter = false,
  // Footer Submit / Cancel button size ("large" by default; a compact inline
  // form passes "small").
  footerButtonSize = "large",
  footerSx,
  validate,
}) {
  const handleOnSubmit = useCallback(
    (formData) => {
      if (onSubmit) {
        onSubmit(formData);
      }
    },
    [onSubmit],
  );

  return (
    <Form
      subscription={{
        submitting: true,
        hasValidationErrors: true,
        errors: true,
        submitFailed: true,
      }}
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      mutators={{ ...arrayMutators }}
      validate={validate}
      render={(formRenderProps) => (
        <RffFormInner
          id={id}
          className={className}
          gap={gap}
          disabled={disabled}
          formRenderProps={formRenderProps}
          children={children}
          errorMessage={errorMessage}
          isLoading={isLoading}
          onCancel={onCancel}
          onCancelText={onCancelText}
          loading={loading}
          formTitle={formTitle}
          titleContent={titleContent}
          titleSize={titleSize}
          titleEndContent={titleEndContent}
          maxWidth={maxWidth}
          formDescription={formDescription}
          suppressFooter={suppressFooter}
          grid={grid}
          includeBottomSpacing={includeBottomSpacing}
          flexDirection={flexDirection}
          startContent={startContent}
          footerStartContent={footerStartContent}
          submitText={submitText}
          sx={sx}
          formSpy={formSpy}
          hideBottomValidationErrors={hideBottomValidationErrors}
          stickyFooter={stickyFooter}
          footerButtonSize={footerButtonSize}
          footerSx={footerSx}
        />
      )}
    />
  );
}

const RffFormInner = memo(function RffFormInner({
  id,
  formRenderProps,
  children,
  errorMessage,
  onCancel,
  loading,
  suppressFooter,
  startContent,
  maxWidth,
  submitText,
  sx,
  onCancelText,
  className,
  flexDirection,
  hideBottomValidationErrors,
  grid,
  disabled,
  footerStartContent,
  formSpy,
  stickyFooter,
  footerButtonSize,
  footerSx,
}) {
  const { handleSubmit, hasValidationErrors, errors, submitFailed } =
    formRenderProps;
  const shouldShowValidationErrors = submitFailed && hasValidationErrors;

  const flattenErrors = (errs, prefix = "") =>
    Object.entries(errs).flatMap(([key, val]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof val === "string") {
        return [path];
      } else if (val && typeof val === "object") {
        return flattenErrors(val, path);
      }
      return [];
    });

  // 2) the new combined message
  const validationErrorMessage =
    shouldShowValidationErrors && errors && !hideBottomValidationErrors
      ? flattenErrors(errors).map((fullPath) => {
          const raw = fullPath.split(".").pop();

          const words = raw
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .toLowerCase();

          return camelCaseToSentence(words);
        })
      : // .join(", ")
        null;

  return (
    <Box
      component="form"
      id={id}
      className={`rff-form-root ${className}`}
      onSubmit={handleSubmit}
      sx={{
        width: "inherit",
        display: "flex",
        flexDirection: "column",
        //
        minHeight: "100%",
        //
        ...sx,
      }}
    >
      {startContent}
      <Container
        maxWidth={maxWidth}
        sx={{
          p: "0 !important",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {grid ? (
          <RffGroup rowSpacing={2.5}>{children}</RffGroup>
        ) : (
          <RffGroupContainer
            sx={{ flexGrow: 1 }}
            flexDirection={flexDirection}
            suppressFooter={suppressFooter}
          >
            {children}
          </RffGroupContainer>
        )}

        <RffFormFooter
          sticky={stickyFooter}
          suppressFooter={suppressFooter}
          onCancel={onCancel}
          onCancelText={onCancelText}
          disabled={disabled}
          loading={loading}
          submitText={submitText}
          footerStartContent={footerStartContent}
          validationErrorMessage={validationErrorMessage}
          footerButtonSize={footerButtonSize}
          footerSx={footerSx}
        />

        <RffFormSpy formSpy={formSpy} />
        <ErrorMessage error={errorMessage}>{errorMessage}</ErrorMessage>
      </Container>
    </Box>
  );
});

const RffFormFooter = memo(function RffFormFooter({
  onCancel,
  disabled,
  loading,
  submitText = "Submit",
  suppressFooter,
  onCancelText = "Cancel",
  footerStartContent,
  validationErrorMessage,
  sticky,
  footerButtonSize = "large",
  // Extra styling for the footer bar (e.g. more space above it).
  footerSx,
}) {
  if (suppressFooter) return null;

  return (
    <Flx
      column
      sx={{
        mt: 1,
        width: "100%",
        ...(sticky && {
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          // Opaque, and bled out to the panel edges so the fields scroll
          // UNDER the bar rather than beside it.
          background: "#ffffff",
          mx: -2,
          px: 2,
          pt: 1.5,
          pb: 1,
          borderTop: "1px solid #eeeeee",
        }),
        ...footerSx,
      }}
    >
      {validationErrorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight={600} color="error">
            Please review and fill out the required fields:
          </Typography>
          <Flx gap={1} wrap sx={{ mt: 1 }}>
            {validationErrorMessage?.map((e) => {
              return (
                <Box
                  key={e}
                  sx={{
                    background: red[50],
                    border: `1px solid ${red[200]}`,
                    borderRadius: 6,
                    px: 1,
                  }}
                >
                  <Typography
                    color="error"
                    key={camelCase(e)}
                    sx={{
                      textTransform: "capitalize",
                      // textAlign: "left",
                    }}
                  >
                    {e}
                  </Typography>
                </Box>
              );
            })}
          </Flx>
        </Alert>
      )}

      <Flx
        fw
        end
        sx={{
          width: "100%",
        }}
      >
        {footerStartContent}
        <Flx>
          {onCancel ? (
            <Button
              variant="outlined"
              color="error"
              size={footerButtonSize}
              onClick={onCancel}
              sx={{ mr: 1 }}
            >
              {onCancelText || "Cancel"}
            </Button>
          ) : null}
          <Button
            disabled={disabled}
            variant="contained"
            color="primary"
            size={footerButtonSize}
            type="submit"
            loading={loading}
          >
            {submitText}
          </Button>
        </Flx>
      </Flx>
    </Flx>
  );
});

const ErrorMessage = memo(function ErrorMessage({ error, children }) {
  if (isNil(error)) {
    return null;
  }
  return <Typography color="error">{children}</Typography>;
});

export default memo(RffForm);
